import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import TvCard from '@/components/feature/TvCard';
import JsonLd from '@/components/base/JsonLd';
import { tvSeriesList, categories, featuredIds } from '@/mocks/tvSeries';

export default function Home() {
  const { t } = useTranslation();

  const featuredSeries = featuredIds
    .map((id) => tvSeriesList.find((s) => s.id === id))
    .filter(Boolean);

  const latestSeries = [...tvSeriesList]
    .sort((a, b) => new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime())
    .slice(0, 8);

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '四虎影视大全 - 首页',
    description: t('siteDescription'),
    url: 'https://www.sihutv.com/',
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      name: '四虎影视大全',
      url: 'https://www.sihutv.com/',
    },
  };

  return (
    <>
      <JsonLd data={webPageSchema} />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[500px] md:h-[650px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20home%20theater%20room%20with%20large%20screen%20displaying%20cinematic%20content%2C%20warm%20ambient%20lighting%2C%20comfortable%20seating%2C%20dark%20walls%20with%20subtle%20geometric%20patterns%2C%20luxury%20entertainment%20space%2C%20professional%20interior%20design%2C%20cozy%20atmosphere%2C%20golden%20and%20amber%20tones&width=1800&height=900&seq=home-hero-bg&orientation=landscape"
            alt="四虎影视大全 - 电视剧全集在线观看"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
          <div className="relative z-10 w-full text-center px-4 md:px-6">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-black text-background-50 mb-4 tracking-wide">
              {t('home.heroTitle')}
            </h1>
            <p className="text-lg md:text-xl text-background-50/90 mb-2 font-heading">
              {t('home.heroSubtitle')}
            </p>
            <p className="text-sm md:text-base text-background-50/70 mb-8 max-w-2xl mx-auto">
              {t('home.heroDesc')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/category/guochan"
                className="px-8 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                {t('home.heroCta')}
              </Link>
              <Link
                to="/about"
                className="px-8 py-3 bg-background-50/10 text-background-50 border border-background-50/30 rounded-full font-medium text-sm hover:bg-background-50/20 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>

        {/* Featured Section */}
        <section className="px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                {t('home.featuredTitle')}
              </h2>
              <p className="text-sm text-foreground-500 max-w-lg mx-auto">
                {t('home.featuredDesc')}
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
              {featuredSeries.map((series) =>
                series ? <TvCard key={series.id} series={series} /> : null
              )}
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section className="px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                {t('home.categoriesTitle')}
              </h2>
              <p className="text-sm text-foreground-500 max-w-lg mx-auto">
                {t('home.categoriesDesc')}
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-3 md:gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.id}`}
                  className="flex flex-col items-center gap-3 p-5 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 hover:-translate-y-1 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-50">
                    <i className={`${cat.icon} text-xl text-primary-500`}></i>
                  </div>
                  <span className="text-sm font-medium text-foreground-800 whitespace-nowrap">
                    {cat.name}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates Section */}
        <section className="px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                  {t('home.latestTitle')}
                </h2>
                <p className="text-sm text-foreground-500">
                  {t('home.latestDesc')}
                </p>
              </div>
              <Link
                to="/category/guochan"
                className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                {t('home.viewAll')}
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm"></i>
                </div>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {latestSeries.map((series) => (
                <TvCard key={series.id} series={series} />
              ))}
            </div>
            <div className="mt-8 text-center sm:hidden">
              <Link
                to="/category/guochan"
                className="inline-flex items-center gap-1 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                {t('home.viewAll')}
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm"></i>
                </div>
              </Link>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative px-4 md:px-6 py-16 md:py-24 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20abstract%20gradient%20background%20with%20soft%20flowing%20shapes%20in%20golden%20and%20coral%20tones%2C%20minimalist%20modern%20design%2C%20gentle%20curves%2C%20atmospheric%20light%2C%20elegant%20and%20inviting%20composition&width=1800&height=600&seq=home-cta-bg&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-background-900/80" />
          <div className="relative z-10 max-w-2xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-4">
              立即开启你的追剧之旅
            </h2>
            <p className="text-sm md:text-base text-background-50/70 mb-8">
              海量电视剧免费全集，无需注册，打开即看。随时随地，畅享精彩剧情。
            </p>
            <Link
              to="/category/guochan"
              className="inline-block px-8 py-3 bg-accent-500 text-background-50 rounded-full font-medium text-sm hover:bg-accent-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              开始观看
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}